import company from '../../data/company.json';
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { InstitutionalNavbar } from '@/components/institutional/InstitutionalNavbar';
import { InstitutionalFooter } from '@/components/institutional/InstitutionalFooter';
import { InfrastructureTrust } from '@/components/institutional/InfrastructureTrust';
import { PageSEO } from '@/components/PageSEO';
import {
  insights,
  insightCategories,
  getCategoryLabel,
  getAudienceLabel,
} from '@/content/insights';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ArrowUpRight } from 'lucide-react';

/**
 * Public /insights index — Phase 100A.
 *
 * Editorial card-based layout consistent with Phase 99A public direction:
 *   - warm paper background
 *   - navy ink
 *   - champagne-gold accent (eyebrow, hover state)
 *   - editorial hero
 *   - readable card grid
 *
 * No "Blog" wording. No retail / AI / yield language. No newsletter signup.
 */

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const InsightCard = ({ article }) => {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to={`/insights/${article.slug}`}
      data-testid={`insight-card-${article.slug}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: 'var(--gpc-paper)',
        border: `1px solid ${hover ? 'var(--gpc-line-strong)' : 'var(--gpc-line)'}`,
        borderRadius: '14px',
        padding: 'clamp(1.5rem, 2.2vw, 1.875rem)',
        textDecoration: 'none',
        transition: 'border-color 0.2s ease, transform 0.25s ease, box-shadow 0.25s ease',
        transform: hover ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hover
          ? '0 6px 22px rgba(19,35,63,0.06)'
          : '0 1px 0 rgba(19,35,63,0.03)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.85rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap' }}>
        <span
          className="gpc-eyebrow"
          style={{ color: 'var(--gpc-ink-soft)', margin: 0 }}
          data-testid="insight-card-category"
        >
          {getCategoryLabel(article.category)}
        </span>
        <span
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: '0.6875rem',
            color: 'var(--gpc-ink-soft)',
            letterSpacing: '0.04em',
          }}
        >
          ·
        </span>
        <span
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: '0.6875rem',
            color: 'var(--gpc-ink-soft)',
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
          }}
          data-testid="insight-card-audience"
        >
          {getAudienceLabel(article.audience)}
        </span>
      </div>

      <h2
        style={{
          fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
          fontWeight: 500,
          fontSize: 'clamp(1.25rem, 1.8vw, 1.5rem)',
          lineHeight: 1.25,
          color: 'var(--gpc-ink)',
          letterSpacing: '-0.005em',
          margin: 0,
        }}
        data-testid="insight-card-title"
      >
        {article.title}
      </h2>

      <p
        style={{
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: '0.9375rem',
          color: 'var(--gpc-ink-soft)',
          lineHeight: 1.6,
          margin: 0,
        }}
      >
        {article.dek}
      </p>

      <div
        style={{
          marginTop: 'auto',
          paddingTop: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
        }}
      >
        <span
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: '0.75rem',
            color: 'var(--gpc-ink-soft)',
          }}
        >
          {formatDate(article.date)} · {article.readTime}
        </span>
        <span
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: hover ? 'var(--gpc-gold)' : 'var(--gpc-ink)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            transition: 'color 0.2s ease',
          }}
        >
          Read
          <ArrowUpRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </Link>
  );
};

export const Insights = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [division, setDivision] = useState('all');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    return insights.filter(a => (division === 'all' || (a.division || 'private-capital') === division) && (activeCategory === 'all' || a.category === activeCategory) && `${a.title} ${a.dek}`.toLowerCase().includes(query.trim().toLowerCase()));
  }, [activeCategory, query, division]);

  return (
    <div className="min-h-screen" style={{ background: 'var(--gpc-paper)' }}>
      <PageSEO
        title={company.insightsTitle}
        description={company.insightsDescription}
        path="/insights"
      />
      <InstitutionalNavbar />

      <main className="pt-20" data-testid="insights-page">
        {/* Hero */}
        <section
          style={{
            background: 'var(--gpc-paper)',
            borderBottom: '1px solid var(--gpc-line-soft)',
          }}
          data-testid="insights-hero"
        >
          <div
            className="max-w-7xl mx-auto"
            style={{ padding: 'clamp(3rem, 6vw, 5rem) clamp(1.5rem, 4vw, 4rem)' }}
          >
            <p className="gpc-eyebrow" style={{ marginBottom: '14px' }}>
              Insights
            </p>
            <h1
              style={{
                fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
                fontWeight: 400,
                fontSize: 'clamp(2.25rem, 4.4vw, 3.4rem)',
                lineHeight: 1.1,
                letterSpacing: '-0.015em',
                color: 'var(--gpc-ink)',
                marginBottom: '14px',
              }}
            >
              Perspectives on property finance and{' '}
              <span style={{ color: 'var(--gpc-gold)', fontStyle: 'italic' }}>private capital.</span>
            </h1>
            <p
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '1.075rem',
                color: 'var(--gpc-ink-soft)',
                maxWidth: '40rem',
                lineHeight: 1.6,
              }}
            >
              Our current library covers Private Capital: market notes, borrower guidance, capital structuring and controlled execution. Home Loans guides will be added following review.
            </p>
          </div>
        </section>

        {/* Filters + cards */}
        <section className="gpc-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8" style={{ paddingBlock: 'clamp(2rem, 4vw, 3rem)' }}>
            <div className="company-actions mb-6" aria-label="Insight divisions">{[["all","All"],["home-loans","Home Loans"],["private-capital","Private Capital"]].map(([key,label])=><button type="button" key={key} aria-pressed={division===key} onClick={()=>{setDivision(key);setActiveCategory("all");}} className={division===key?"gpc-cta-primary":"gpc-cta-secondary"}>{label}</button>)}</div>
            <div className="max-w-lg mb-6"><Label htmlFor="insight-search">Search Insights</Label><Input id="insight-search" type="search" value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search titles and topics" className="mt-2"/><p role="status" className="text-sm mt-2" style={{color:"var(--gpc-ink-soft)"}}>{filtered.length} articles</p></div>{/* Category filter pills */}
            <div
              className="flex items-center gap-2 flex-wrap"
              style={{ marginBottom: '2rem' }}
              data-testid="insights-category-filters"
            >
              {(division === "home-loans" ? [] : insightCategories).map((cat) => {
                const active = cat.key === activeCategory;
                return (
                  <button
                    key={cat.key}
                    type="button"
                    onClick={() => setActiveCategory(cat.key)} aria-pressed={activeCategory === cat.key}
                    data-testid={`insights-filter-${cat.key}`}
                    style={{
                      fontFamily: 'Inter, system-ui, sans-serif',
                      fontSize: '0.8125rem',
                      fontWeight: active ? 600 : 500,
                      padding: '0.45rem 0.95rem',
                      borderRadius: '999px',
                      border: `1px solid ${active ? 'var(--gpc-ink)' : 'var(--gpc-line)'}`,
                      background: active ? 'var(--gpc-ink)' : 'var(--gpc-paper)',
                      color: active ? 'var(--gpc-paper)' : 'var(--gpc-ink-soft)',
                      cursor: 'pointer',
                      transition: 'background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease',
                    }}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </div>

            {/* Cards / empty state */}
            {filtered.length === 0 ? (
              <div
                data-testid="insights-empty-state"
                style={{
                  background: 'var(--gpc-panel)',
                  border: '1px dashed var(--gpc-line)',
                  borderRadius: '14px',
                  padding: '3rem',
                  textAlign: 'center',
                }}
              >
                <p
                  style={{
                    fontFamily: 'Fraunces, Georgia, serif',
                    fontSize: '1.125rem',
                    color: 'var(--gpc-ink)',
                    marginBottom: '0.5rem',
                  }}
                >
                  {division === "home-loans" ? "Home Loans guides are in preparation." : "No notes in this category yet."}
                </p>
                <p
                  style={{
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: '0.875rem',
                    color: 'var(--gpc-ink-soft)',
                  }}
                >
                  Try a different category — or browse all insights.
                </p>
              </div>
            ) : (
              <div
                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
                data-testid="insights-grid"
              >
                {filtered.map((article) => (
                  <InsightCard key={article.slug} article={article} />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <InfrastructureTrust />
      <InstitutionalFooter />
    </div>
  );
};

export default Insights;
